/**
 * Discovery Redux Slice
 * Manages workspace discovery and join request state
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DiscoverableWorkspace,
  WorkspaceJoinRequest,
  WorkspaceType,
  JoinRequestStatus,
  DiscoverWorkspacesRequest,
  SubmitJoinRequestRequest,
  JoinRequestQueryParams,
} from '../../api/types/DiscoveryTypes';
import { discoveryApi } from '../../api/endpoints/discoveryApi';

// ==================== State Interface ====================

interface DiscoveryState {
  // Discoverable workspaces
  workspaces: DiscoverableWorkspace[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  searchQuery: string;
  typeFilter: WorkspaceType | null;

  // Join requests (for admin view)
  workspaceJoinRequests: WorkspaceJoinRequest[];

  // User's own join requests
  myJoinRequests: WorkspaceJoinRequest[];

  // UI state
  loading: boolean;
  error: string | null;
  loadingJoinRequests: boolean;
  loadingMyRequests: boolean;
  submittingRequest: boolean;
  processingRequestId: string | null; // For approve/reject actions
}

const initialState: DiscoveryState = {
  workspaces: [],
  total: 0,
  hasMore: false,
  currentPage: 0,
  searchQuery: '',
  typeFilter: null,
  workspaceJoinRequests: [],
  myJoinRequests: [],
  loading: false,
  error: null,
  loadingJoinRequests: false,
  loadingMyRequests: false,
  submittingRequest: false,
  processingRequestId: null,
};

// ==================== Async Thunks ====================

/**
 * Discover workspaces with search and filters
 */
export const discoverWorkspaces = createAsyncThunk(
  'discovery/discoverWorkspaces',
  async (params: DiscoverWorkspacesRequest) => {
    const response = await discoveryApi.discoverWorkspaces(params);
    return response;
  },
);

/**
 * Load more workspaces (pagination)
 */
export const loadMoreWorkspaces = createAsyncThunk(
  'discovery/loadMoreWorkspaces',
  async (params: DiscoverWorkspacesRequest) => {
    const response = await discoveryApi.discoverWorkspaces(params);
    return response;
  },
);

/**
 * Join an open workspace instantly
 */
export const joinOpenWorkspace = createAsyncThunk(
  'discovery/joinOpenWorkspace',
  async (workspaceId: string) => {
    const response = await discoveryApi.joinOpenWorkspace(workspaceId);
    return { workspaceId, ...response };
  },
);

/**
 * Submit a join request for a closed workspace
 */
export const submitJoinRequest = createAsyncThunk(
  'discovery/submitJoinRequest',
  async ({
    workspaceId,
    data,
  }: {
    workspaceId: string;
    data: SubmitJoinRequestRequest;
  }) => {
    const response = await discoveryApi.submitJoinRequest(workspaceId, data);
    return { workspaceId, ...response };
  },
);

/**
 * Fetch join requests for a workspace (admin)
 */
export const fetchWorkspaceJoinRequests = createAsyncThunk(
  'discovery/fetchWorkspaceJoinRequests',
  async ({
    workspaceId,
    params,
  }: {
    workspaceId: string;
    params?: JoinRequestQueryParams;
  }) => {
    const requests = await discoveryApi.getWorkspaceJoinRequests(
      workspaceId,
      params,
    );
    return requests;
  },
);

/**
 * Fetch user's own join requests
 */
export const fetchMyJoinRequests = createAsyncThunk(
  'discovery/fetchMyJoinRequests',
  async (params?: JoinRequestQueryParams) => {
    const requests = await discoveryApi.getMyJoinRequests(params);
    return requests;
  },
);

/**
 * Approve a join request
 */
export const approveJoinRequest = createAsyncThunk(
  'discovery/approveJoinRequest',
  async (requestId: string) => {
    const response = await discoveryApi.approveJoinRequest(requestId);
    return { requestId, ...response };
  },
);

/**
 * Reject a join request
 */
export const rejectJoinRequest = createAsyncThunk(
  'discovery/rejectJoinRequest',
  async (requestId: string) => {
    const response = await discoveryApi.rejectJoinRequest(requestId);
    return { requestId, ...response };
  },
);

/**
 * Cancel a join request
 */
export const cancelJoinRequest = createAsyncThunk(
  'discovery/cancelJoinRequest',
  async (requestId: string) => {
    await discoveryApi.cancelJoinRequest(requestId);
    return requestId;
  },
);

// ==================== Slice ====================

const discoverySlice = createSlice({
  name: 'discovery',
  initialState,
  reducers: {
    // Set search query
    setSearchQuery(state, action: PayloadAction<string>) {
      state.searchQuery = action.payload;
    },

    // Set type filter
    setTypeFilter(state, action: PayloadAction<WorkspaceType | null>) {
      state.typeFilter = action.payload;
    },

    // Reset discovery state
    resetDiscovery(state) {
      state.workspaces = [];
      state.total = 0;
      state.hasMore = false;
      state.currentPage = 0;
      state.searchQuery = '';
      state.typeFilter = null;
      state.error = null;
    },

    // Clear error
    clearError(state) {
      state.error = null;
    },

    // Mark workspace as joined (optimistic update)
    markWorkspaceAsJoined(state, action: PayloadAction<string>) {
      const workspace = state.workspaces.find((w) => w.id === action.payload);
      if (workspace) {
        // Remove from discovery list after joining
        state.workspaces = state.workspaces.filter(
          (w) => w.id !== action.payload,
        );
        state.total = Math.max(0, state.total - 1);
      }
    },
  },
  extraReducers: (builder) => {
    // ==================== Discover Workspaces ====================
    builder
      .addCase(discoverWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(discoverWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaces = action.payload.workspaces;
        state.total = action.payload.total;
        state.hasMore = action.payload.hasMore;
        state.currentPage = 0;
      })
      .addCase(discoverWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to discover workspaces';
      });

    // ==================== Load More Workspaces ====================
    builder
      .addCase(loadMoreWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMoreWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        // Append new workspaces
        state.workspaces = [...state.workspaces, ...action.payload.workspaces];
        state.total = action.payload.total;
        state.hasMore = action.payload.hasMore;
        state.currentPage += 1;
      })
      .addCase(loadMoreWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load more workspaces';
      });

    // ==================== Join Open Workspace ====================
    builder
      .addCase(joinOpenWorkspace.pending, (state) => {
        state.submittingRequest = true;
        state.error = null;
      })
      .addCase(joinOpenWorkspace.fulfilled, (state, action) => {
        state.submittingRequest = false;
        // Remove workspace from discovery list
        state.workspaces = state.workspaces.filter(
          (w) => w.id !== action.payload.workspaceId,
        );
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(joinOpenWorkspace.rejected, (state, action) => {
        state.submittingRequest = false;
        state.error = action.error.message || 'Failed to join workspace';
      });

    // ==================== Submit Join Request ====================
    builder
      .addCase(submitJoinRequest.pending, (state) => {
        state.submittingRequest = true;
        state.error = null;
      })
      .addCase(submitJoinRequest.fulfilled, (state, action) => {
        state.submittingRequest = false;
        // Update workspace to show pending request
        const workspace = state.workspaces.find(
          (w) => w.id === action.payload.workspaceId,
        );
        if (workspace) {
          workspace.hasPendingRequest = true;
        }
        // Add to myJoinRequests
        state.myJoinRequests = [
          action.payload.joinRequest,
          ...state.myJoinRequests,
        ];
      })
      .addCase(submitJoinRequest.rejected, (state, action) => {
        state.submittingRequest = false;
        state.error = action.error.message || 'Failed to submit join request';
      });

    // ==================== Fetch Workspace Join Requests ====================
    builder
      .addCase(fetchWorkspaceJoinRequests.pending, (state) => {
        state.loadingJoinRequests = true;
        state.error = null;
      })
      .addCase(fetchWorkspaceJoinRequests.fulfilled, (state, action) => {
        state.loadingJoinRequests = false;
        state.workspaceJoinRequests = action.payload;
      })
      .addCase(fetchWorkspaceJoinRequests.rejected, (state, action) => {
        state.loadingJoinRequests = false;
        state.error = action.error.message || 'Failed to fetch join requests';
      });

    // ==================== Fetch My Join Requests ====================
    builder
      .addCase(fetchMyJoinRequests.pending, (state) => {
        state.loadingMyRequests = true;
        state.error = null;
      })
      .addCase(fetchMyJoinRequests.fulfilled, (state, action) => {
        state.loadingMyRequests = false;
        state.myJoinRequests = action.payload;
      })
      .addCase(fetchMyJoinRequests.rejected, (state, action) => {
        state.loadingMyRequests = false;
        state.error = action.error.message || 'Failed to fetch my join requests';
      });

    // ==================== Approve Join Request ====================
    builder
      .addCase(approveJoinRequest.pending, (state, action) => {
        state.processingRequestId = action.meta.arg;
      })
      .addCase(approveJoinRequest.fulfilled, (state, action) => {
        state.processingRequestId = null;
        // Update request status in both lists
        const updateRequest = (request: WorkspaceJoinRequest) => {
          if (request.id === action.payload.requestId) {
            return {
              ...request,
              status: JoinRequestStatus.APPROVED,
              respondedAt: action.payload.joinRequest.respondedAt,
              respondedBy: action.payload.joinRequest.respondedBy,
            };
          }
          return request;
        };
        state.workspaceJoinRequests = state.workspaceJoinRequests.map(updateRequest);
        state.myJoinRequests = state.myJoinRequests.map(updateRequest);
      })
      .addCase(approveJoinRequest.rejected, (state, action) => {
        state.processingRequestId = null;
        state.error = action.error.message || 'Failed to approve join request';
      });

    // ==================== Reject Join Request ====================
    builder
      .addCase(rejectJoinRequest.pending, (state, action) => {
        state.processingRequestId = action.meta.arg;
      })
      .addCase(rejectJoinRequest.fulfilled, (state, action) => {
        state.processingRequestId = null;
        // Update request status in both lists
        const updateRequest = (request: WorkspaceJoinRequest) => {
          if (request.id === action.payload.requestId) {
            return {
              ...request,
              status: JoinRequestStatus.REJECTED,
              respondedAt: action.payload.joinRequest.respondedAt,
              respondedBy: action.payload.joinRequest.respondedBy,
            };
          }
          return request;
        };
        state.workspaceJoinRequests = state.workspaceJoinRequests.map(updateRequest);
        state.myJoinRequests = state.myJoinRequests.map(updateRequest);
      })
      .addCase(rejectJoinRequest.rejected, (state, action) => {
        state.processingRequestId = null;
        state.error = action.error.message || 'Failed to reject join request';
      });

    // ==================== Cancel Join Request ====================
    builder
      .addCase(cancelJoinRequest.pending, (state, action) => {
        state.processingRequestId = action.meta.arg;
      })
      .addCase(cancelJoinRequest.fulfilled, (state, action) => {
        state.processingRequestId = null;
        // Remove from myJoinRequests
        state.myJoinRequests = state.myJoinRequests.filter(
          (r) => r.id !== action.payload,
        );
        // Update workspace to remove pending request flag
        const workspace = state.workspaces.find(
          (w) =>
            state.myJoinRequests.find((r) => r.id === action.payload)
              ?.workspaceId === w.id,
        );
        if (workspace) {
          workspace.hasPendingRequest = false;
        }
      })
      .addCase(cancelJoinRequest.rejected, (state, action) => {
        state.processingRequestId = null;
        state.error = action.error.message || 'Failed to cancel join request';
      });
  },
});

// ==================== Exports ====================

export const {
  setSearchQuery,
  setTypeFilter,
  resetDiscovery,
  clearError,
  markWorkspaceAsJoined,
} = discoverySlice.actions;

export default discoverySlice.reducer;
